import {
  Pin,
  PinComponent,
  PinComponentId,
  PinId,
  isTextPinComponent,
  processPinComponentTipTapFields,
  processPinTipTapFields,
  tipTapSchema,
} from '@lessonup/pins-shared';
import { isEqual } from 'lodash';
import { Node } from 'prosemirror-model';
import { prosemirrorJSONToYXmlFragment, yXmlFragmentToProsemirrorJSON } from 'y-prosemirror';
import { Doc as YDoc, Map as YMap, XmlFragment as YXmlFragment } from 'yjs';

export const deletePinFragments = (yDoc: YDoc, pin: Pin): void => {
  const pinsMap = getFragmentsRoot(yDoc);
  pinsMap.delete(pin.id);
};

export function deletePinComponentFragments(yDoc: YDoc, pinId: PinId, pinComponentIds: PinComponentId[]): void {
  const pinsMap = getFragmentsRoot(yDoc);
  const pinComponentsMap = pinsMap.get(pinId);
  if (!pinComponentsMap) {
    return;
  }
  for (const pinComponentId of pinComponentIds) {
    pinComponentsMap.delete(pinComponentId);
  }
}

/**
 * Mutates the pinComponents by setting the tipTapContent to the JSON representation of the fragment
 */
export function copyTipTapFragmentToComponents(yDoc: YDoc, pinId: PinId, pinComponents: PinComponent[]): void {
  pinComponents.forEach((pinComponent) => {
    processPinComponentTipTapFields(pinComponent, ({ componentId, fragmentKey }) => {
      return getJSONFromFragment(yDoc, pinId, componentId, fragmentKey);
    });
  });
}

/**
 * Mutates the pins and pinComponents by setting the tipTapContent to the JSON representation of the fragment
 */
export function copyTipTapFragmentToPins(yDoc: YDoc, pins: Pin[]): void {
  pins.forEach((pin) => {
    processPinTipTapFields(pin, ({ componentId, fragmentKey }) => {
      return getJSONFromFragment(yDoc, pin.id, componentId, fragmentKey);
    });
  });
}

/**
 * Mutates the yDoc by setting the fragment to the JSON representation of the tipTapContent
 */
export function moveTipTapContentToFragments(yDoc: YDoc, pin: Pin): void {
  processPinTipTapFields(pin, ({ tipTapContent, componentId, fragmentKey }) => {
    if (tipTapContent !== undefined) {
      const fragment = getOrCreatePinComponentFragment(yDoc, pin.id, componentId, fragmentKey);
      prosemirrorJSONToYXmlFragment(tipTapSchema, tipTapContent, fragment);
    }
  });
}

export function getOrCreatePinComponentFragment(
  yDoc: YDoc,
  pinId: PinId,
  pinComponentId: PinComponentId,
  fragmentKey = 'f0'
) {
  return (
    getPinComponentFragment(yDoc, pinId, pinComponentId, fragmentKey) ??
    createPinComponentFragment(yDoc, pinId, pinComponentId, fragmentKey)
  );
}

function createPinComponentFragment(yDoc: YDoc, pinId: PinId, pinComponentId: PinComponentId, fragmentKey = 'f0') {
  const pinsMap = getFragmentsRoot(yDoc);
  const componentsMap = pinsMap.get(pinId) || pinsMap.set(pinId, new YMap<YMap<YXmlFragment>>());
  const fragmentsMap = componentsMap.get(pinComponentId) || componentsMap.set(pinComponentId, new YMap<YXmlFragment>());

  return fragmentsMap.get(fragmentKey) || fragmentsMap.set(fragmentKey, new YXmlFragment());
}

/**
 * Fragment is nested 3 levels deep in yDoc: yDoc -> fragments (Map) -> pinId (Map) -> pinComponentId (Map) -> fragmentKey (XmlFragment)
 * fragmentKey exists for future-proofing support multiple fragments per pinComponentId (e.g. multiple text pin components)
 */
export function getPinComponentFragment(
  yDoc: YDoc,
  pinId: PinId,
  pinComponentId: PinComponentId,
  fragmentKey = 'f0'
): YXmlFragment | null {
  const pinsMap = getFragmentsRoot(yDoc);
  const componentsMap = pinsMap.get(pinId);
  const fragmentsMap = componentsMap?.get(pinComponentId);
  return fragmentsMap?.get(fragmentKey) || null;
}

export function isLayoutPinComponentChanged(
  yDoc: YDoc,
  pinId: PinId,
  layoutPinComponent: PinComponent,
  pinComponent: PinComponent
): boolean {
  if (isTextPinComponent(layoutPinComponent) && isTextPinComponent(pinComponent)) {
    const currentJSon = getJSONFromFragment(yDoc, pinId, pinComponent.id);
    return !isEqual(currentJSon, layoutPinComponent.settings.tipTapContent);
  }

  return !isEqual(pinComponent.layout, layoutPinComponent.layout);
}

export function getJSONFromFragment(yDoc: YDoc, pinId: PinId, pinComponentId: PinComponentId, fragmentKey = 'f0') {
  const fragment = getPinComponentFragment(yDoc, pinId, pinComponentId, fragmentKey) ?? new YXmlFragment();

  return yXmlFragmentToProsemirrorJSON(fragment);
}

export function getFragmentsRoot(yDoc: YDoc): YMap<YMap<YMap<YXmlFragment>>> {
  return yDoc.getMap<YMap<YMap<YXmlFragment>>>('fragments');
}

export function getDataRoot(yDoc: YDoc): YMap<unknown> {
  return yDoc.getMap('data');
}

export function getProsemirrorNode(jsonState: object): Node {
  return Node.fromJSON(tipTapSchema, jsonState);
}

export function tipTapContentToText(jsonState: object): string {
  const doc = getProsemirrorNode(jsonState);
  return doc.textBetween(0, doc.content.size, '\n');
}
